import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import {
  LayoutDashboard, School, Users, ClipboardList, LogOut,
  ShieldCheck, Menu, X, ChevronRight,
} from "lucide-react";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { logoutSuperAdmin } from "@/hooks/services/superAdminAuthService";

const NAV = [
  { label: "Dashboard",  path: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Schools",    path: "/admin/schools",   icon: School },
  { label: "Users",      path: "/admin/users",     icon: Users },
  { label: "Audit Log",  path: "/admin/audit",     icon: ClipboardList },
];

export default function SuperAdminLayout({ children }: { children: React.ReactNode }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { currentUser } = AuthStore();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logoutSuperAdmin();
    navigate("/admin/login");
  };

  const Sidebar = () => (
    <aside className="flex flex-col h-full bg-slate-900 border-r border-slate-800 w-60">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
        <div className="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/30 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-teal-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">EarlyScape</p>
          <p className="text-xs text-slate-500">Super Admin</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ label, path, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active
                  ? "bg-teal-500/10 text-teal-400 border border-teal-500/20"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
              {active && <ChevronRight className="w-3.5 h-3.5 ml-auto text-teal-500" />}
            </Link>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-3 pb-5 pt-3 border-t border-slate-800 space-y-2">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-3.5 h-3.5 text-teal-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-white truncate">{currentUser?.name ?? "Super Admin"}</p>
            <p className="text-xs text-slate-500 truncate">{currentUser?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-950 text-white overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex h-full">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative flex h-full w-60">
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <div className="flex md:hidden items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900">
          <button onClick={() => setOpen(true)} className="text-slate-400 hover:text-white">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-white">Admin Panel</span>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white opacity-0 pointer-events-none">
            <X className="w-5 h-5" />
          </button>
        </div>

        <main className="flex-1 overflow-y-auto p-5 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
